/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd. All rights reserved.
This file contains Protected Intellectual Property owned by Kaleida Ltd and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (explicitly not developed by Kaleida), whether or not such application forms part of the Platform.
For the avoidance of doubt, the inclusion of any application within the Platform does not of itself grant any right to use Protected Intellectual Property in that application unless that application was developed by Kaleida.
*/

// IP-NOTICE: PROTECTED
export enum UploadVariant {
	Standard = 'standard',
	Compact = 'compact',
	Image = 'image',
}

export enum UploadIconLibrary {
	/** Backwards-compatible Material name input; rendering is normalised to Lucide. */
	Material = 'material',
	Lucide = 'lucide',
}

export type UploadImageFit = 'contain' | 'cover';

export interface UploadConfiguration {
	variant?: UploadVariant;
	label?: string;
	helpText?: string;
	supportingMessage?: string;
	uploadPrompt?: string;
	dropPrompt?: string;
	showIcon?: boolean;
	uploadIcon?: string;
	dropIcon?: string;
	uploadIconLibrary?: UploadIconLibrary;
	dropIconLibrary?: UploadIconLibrary;
	allowedExtensions?: string[];
	allowedMimeTypes?: string[];
	maximumFileSizeMb?: number;
	maximumFileCount?: number;
	required?: boolean;
	backgroundColour?: string;
	hoverBackgroundColour?: string;
	hoverBorderColour?: string;
	focusColour?: string;
	selectedBackgroundColour?: string;
	/** Preferred UAT terminology. selectedBackgroundColour remains as a backwards-compatible alias. */
	previewBackground?: string;
	selectedRowBackgroundColour?: string;
	imageFit?: UploadImageFit;
}

export interface ExistingUpload {
	id: string | number;
	name: string;
	previewUrl?: string;
	size?: number;
	mimeType?: string;
}

export interface UploadItem {
	key: string;
	name: string;
	extension: string;
	size: number;
	mimeType: string;
	previewUrl?: string;
	file?: File;
	existing?: ExistingUpload;
}

export type UploadValue = File | ExistingUpload | Array<File | ExistingUpload> | null;

export type UploadValidationReason = 'fileType' | 'fileSize' | 'fileCount' | 'duplicate';

export interface UploadValidationFailure {
	file: File;
	reason: UploadValidationReason;
	message: string;
}

export const DEFAULT_UPLOAD_CONFIGURATION: Required<UploadConfiguration> = {
	variant: UploadVariant.Standard,
	label: '',
	helpText: '',
	supportingMessage: '',
	uploadPrompt: 'Click or drag a file to upload',
	dropPrompt: 'Drop to upload',
	showIcon: true,
	uploadIcon: 'cloud_upload',
	dropIcon: 'move_to_inbox',
	uploadIconLibrary: UploadIconLibrary.Lucide,
	dropIconLibrary: UploadIconLibrary.Lucide,
	allowedExtensions: [],
	allowedMimeTypes: [],
	maximumFileSizeMb: 10,
	maximumFileCount: 1,
	required: false,
	backgroundColour: '#FBFAF4',
	hoverBackgroundColour: '#FFFFFF',
	hoverBorderColour: '#68F7F5',
	focusColour: '#68F7F5',
	selectedBackgroundColour: '#1A1935',
	previewBackground: '#1A1935',
	selectedRowBackgroundColour: '#EEEADB',
	imageFit: 'contain',
};
